import React, { useEffect, useState } from 'react';
import { Button, Col, Form, FormInstance, Grid, Row, Upload, UploadFile } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { flatten } from 'lodash';

import { DeleteOutlined, DownloadOutlined, PaperClipOutlined } from '@ant-design/icons';

import { getFormFieldData } from '../../common/filterHelpers';
import { SearchFormFieldEnum } from '../../common/searchFormField.types';
import { ContractTypeEnum, LevelEnum, PriorityEnum, RemotePreferenceEnum } from '../../common/types';
import { useGetAllClients } from '../../common/useGetAllClients';
import { useGetAllUsers } from '../../common/useGetAllUsers';
import { useGetClientEmployees } from '../../common/useGetClientEmployees';
import { useGetFileStorageFile } from '../../common/useGetFileStorageFile';
import { useGetFileStorageToken } from '../../common/useGetFilestorageToken';
import { FormCheckbox } from '../../components/forms/FormCheckbox';
import { FormCheckboxGroup } from '../../components/forms/FormCheckboxGroup';
import { FormDatePicker } from '../../components/forms/FormDatePicker';
import { FormInput } from '../../components/forms/FormInput';
import { FormSelect } from '../../components/forms/FormSelect';
import { FormSelectMultiple } from '../../components/forms/FormSelectMultiple';
import { FormTextArea } from '../../components/forms/FormTextArea';
import { StyledRow } from '../../components/StyledRow';
import { AddIcon } from '../../components/TableWithDataUpdate';
import { useGetDictionaryByName } from '../dictionaries/useGetDictionaryByName';
import { ProjectType } from '../projects/ProjectsTable.types';

const HOURLY_RATE_NOTE_MAX_LENGTH = 1000;

export const mapErrorFieldsToFormLabels = {
  backoffices: 'Backoffices',
  coordinators: 'Coordinators',
  interviewers: 'Interviewers',
  recruiters: 'Recruiters',
  client: 'Client',
  clientEmployee: 'Employee',
  description: 'Description',
  externalId: 'External ID',
  experienceYears: 'Experience (years)',
  levels: 'Levels',
  musts: 'Musts',
  preferredStartDate: 'Preferred start date',
  clientHourlyRateMin: 'Client min (PLN/h)',
  clientHourlyRateMax: 'Client max (PLN/h)',
  candidateHourlyRateMin: 'Candidate min (PLN/h)',
  candidateHourlyRateMax: 'Candidate max (PLN/h)',
  hourlyRateNote: 'Hourly rate note',
};

type ProjectDataProps = {
  form: FormInstance;
  attachments: UploadFile[];
  onAttachmentsChange: (attachments: UploadFile[]) => void;
  initialData?: ProjectType;
};

type FormValuesType = {
  active?: boolean;
  client?: string;
  clientEmployee?: string;
  externalId?: string;
  contractTypes?: ContractTypeEnum[];
  levels?: LevelEnum[];
  preferredStartDate?: Dayjs;
  priority?: PriorityEnum;
  clientHourlyRateMin?: number;
  clientHourlyRateMax?: number;
  candidateHourlyRateMin?: number;
  candidateHourlyRateMax?: number;
  hourlyRateNote?: string;
  remotePreference?: RemotePreferenceEnum;
  coordinators?: number[];
  backoffices?: number[];
  recruiters?: number[];
  interviewers?: number[];
};

export const mapProjectDataToFormValues = (initialData?: ProjectType): FormValuesType => ({
  ...initialData,
  active: initialData ? initialData.active : true,
  client: initialData?.client?.uuid,
  clientEmployee: initialData?.clientEmployee?.uuid,
  externalId: initialData?.externalId ?? undefined,
  preferredStartDate: initialData?.preferredStartDate ? dayjs(initialData.preferredStartDate) : undefined,
  levels: initialData?.levels,
  priority: initialData?.priority,
  clientHourlyRateMin: initialData?.clientHourlyRateMin ?? undefined,
  clientHourlyRateMax: initialData?.clientHourlyRateMax ?? undefined,
  candidateHourlyRateMin: initialData?.candidateHourlyRateMin ?? undefined,
  candidateHourlyRateMax: initialData?.candidateHourlyRateMax ?? undefined,
  hourlyRateNote: initialData?.hourlyRateNote ?? undefined,
  remotePreference: initialData?.remotePreference,
  coordinators: initialData?.coordinators?.map((coordinator) => coordinator.id),
  backoffices: initialData?.backoffices?.map((backoffice) => backoffice.id),
  recruiters: initialData?.recruiters?.map((recruiter) => recruiter.id),
  interviewers: initialData?.interviewers?.map((interviewer) => interviewer.id),
  contractTypes: initialData?.contractTypes
    ? (getFormFieldData(SearchFormFieldEnum.ContractTypes).toFormValueFormatter?.(
        flatten([initialData.contractTypes])
      ) as ContractTypeEnum[])
    : [],
});

export const mapPreferredStartDateToRequest = (preferredStartDate?: Dayjs | null) =>
  preferredStartDate ? dayjs(preferredStartDate).format('YYYY-MM-DD') : null;

export const normalizeOptionalRate = (value?: string | number | null) => {
  if (value === undefined || value === null || value === '') return undefined;

  const numberValue = Number(value);

  return Number.isFinite(numberValue) ? numberValue : undefined;
};

export const normalizeOptionalText = (value?: string | null) => {
  const trimmedValue = value?.trim();

  return trimmedValue || undefined;
};

export const validateNonNegativeRate = (value?: number) => value === undefined || value >= 0;

export const validateRateRange = (min?: number, max?: number) =>
  min !== undefined && max !== undefined && min > max
    ? 'Maximum rate must be greater than or equal to minimum rate'
    : undefined;

const nonNegativeRateRule = [
  {
    validator: (_: unknown, value?: number) => {
      if (validateNonNegativeRate(value)) return Promise.resolve();

      return Promise.reject(new Error('Rate cannot be negative'));
    },
  },
];

const getMaxRateRules = (form: FormInstance, minFieldName: string) => [
  {
    validator: (_: unknown, value?: number) => {
      if (!validateNonNegativeRate(value)) return Promise.reject(new Error('Rate cannot be negative'));

      const rangeError = validateRateRange(form.getFieldValue(minFieldName), value);
      return rangeError ? Promise.reject(new Error(rangeError)) : Promise.resolve();
    },
  },
];

export const ProjectDataForm: React.FC<ProjectDataProps> = ({
  form,
  attachments,
  onAttachmentsChange,
  initialData,
}) => {
  const screens = Grid.useBreakpoint();

  const { data: clients } = useGetAllClients();
  const { data: users } = useGetAllUsers();
  const { data: technologies } = useGetDictionaryByName('technologies');

  const currentClient = Form.useWatch('client', form);
  const { data: employees } = useGetClientEmployees(currentClient);

  const priorityOptions = Object.entries(PriorityEnum).map((value) => ({
    content: value[1],
    value: value[0],
  }));

  const clientOptions = (clients?.results ?? []).map((client) => ({
    content: client.name,
    value: client.uuid,
  }));

  const clientEmplyeesOptions = (employees ?? []).map((employee) => ({
    content: `${employee.firstName} ${employee.lastName}`,
    value: employee.uuid,
  }));

  const userOptions = (users?.results ?? []).map((user) => ({
    content: `${user.firstName} ${user.lastName}`,
    value: user.id,
  }));

  const technologyOptions = (technologies ?? []).map((technology) => ({
    content: technology.value,
    value: technology.value,
  }));

  const remotePreferenceOptions = Object.entries(RemotePreferenceEnum).map((preference) => ({
    value: preference[0],
    content: preference[1],
  }));

  const levelOptions = Object.entries(LevelEnum).map((level) => ({
    value: level[0],
    content: level[1],
  }));

  const contractTypeOptions = Object.values(ContractTypeEnum);

  const [downloadedFileId, setDownloadedFileId] = useState<string | null>(null);

  const { data: token } = useGetFileStorageToken(downloadedFileId ?? '');
  const { data: downloadedFile, isLoading: fileLoading } = useGetFileStorageFile(downloadedFileId ?? '', token?.token);

  useEffect(() => {
    if (downloadedFile && !fileLoading) {
      const file = attachments.find((attachment) => attachment.uid === downloadedFileId);
      triggerDownload({ blob: downloadedFile, filename: file?.name || 'attachment' });
      setDownloadedFileId(null);
    }
  }, [attachments, downloadedFile, downloadedFileId, fileLoading]);

  const initialValues = mapProjectDataToFormValues(initialData);

  return (
    <Form form={form} initialValues={initialValues} layout="vertical" colon={false}>
      <Row gutter={[15, 15]}>
        <Col xs={24} lg={12} xl={11}>
          <Row gutter={[15, 5]}>
            <Col xs={24} sm={16}>
              <FormInput name="name" label="Name" required />
            </Col>
            <Col xs={24} sm={8}>
              <FormInput name="externalId" label="External ID" />
            </Col>
            <Col xs={24} md={8}>
              <FormSelect name="client" label="Client" options={clientOptions} required />
            </Col>
            <Col xs={24} md={8}>
              <FormSelect name="clientEmployee" label="Employee" options={clientEmplyeesOptions} required />
            </Col>
            <Col xs={24} md={8}>
              <FormSelect name="priority" label="Priority" options={priorityOptions} required allowClear={false} />
            </Col>
            <Col xs={24} md={8}>
              <FormInput name="experience" label="Experience (years)" required />
            </Col>
            <Col xs={24} md={8}>
              <FormSelectMultiple name="levels" label="Level" options={levelOptions} required />
            </Col>
            <Col xs={24} md={8}>
              <FormDatePicker name="preferredStartDate" label="Preferred start" />
            </Col>
            <Col xs={24} sm={12}>
              <FormInput
                name="clientHourlyRateMin"
                label="Client min (PLN/h)"
                type="number"
                normalize={normalizeOptionalRate}
                rules={nonNegativeRateRule}
              />
            </Col>
            <Col xs={24} sm={12}>
              <FormInput
                name="clientHourlyRateMax"
                label="Client max (PLN/h)"
                type="number"
                normalize={normalizeOptionalRate}
                rules={getMaxRateRules(form, 'clientHourlyRateMin')}
                dependencies={['clientHourlyRateMin']}
              />
            </Col>
            <Col xs={24} sm={12}>
              <FormInput
                name="candidateHourlyRateMin"
                label="Candidate min (PLN/h)"
                type="number"
                normalize={normalizeOptionalRate}
                rules={nonNegativeRateRule}
              />
            </Col>
            <Col xs={24} sm={12}>
              <FormInput
                name="candidateHourlyRateMax"
                label="Candidate max (PLN/h)"
                type="number"
                normalize={normalizeOptionalRate}
                rules={getMaxRateRules(form, 'candidateHourlyRateMin')}
                dependencies={['candidateHourlyRateMin']}
              />
            </Col>
            <Col xs={24}>
              <FormInput
                name="hourlyRateNote"
                label="Hourly rate note"
                maxLength={HOURLY_RATE_NOTE_MAX_LENGTH}
                rules={[
                  {
                    max: HOURLY_RATE_NOTE_MAX_LENGTH,
                    message: `Hourly rate note must be at most ${HOURLY_RATE_NOTE_MAX_LENGTH} characters`,
                  },
                ]}
              />
            </Col>
            <Col xs={24} md={8}>
              <FormInput name="city" label="Project city" />
            </Col>
            <Col xs={24} md={8}>
              <FormSelect name="remotePreference" label="Remote preferences" options={remotePreferenceOptions} />
            </Col>
            <Col xs={24} md={8}>
              <FormInput name="remoteDays" label="Remote days" />
            </Col>
            <Col xs={24}>
              <FormCheckboxGroup
                name="contractTypes"
                label="Supported contract"
                options={contractTypeOptions}
                required
                vertical={screens.xs || (screens.sm && !screens.md)}
              />
            </Col>
            <Col xs={24}>
              <FormSelectMultiple
                name="musts"
                label="Technologies required (primary)"
                mode="tags"
                options={technologyOptions}
                required
              />
            </Col>
            <Col xs={24}>
              <FormSelectMultiple
                name="nices"
                label="Technologies required (additional)"
                mode="tags"
                options={technologyOptions}
              />
            </Col>

            <Col xs={24} sm={12} xl={6}>
              <FormSelectMultiple name="coordinators" label="Coordinator" options={userOptions} required />
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <FormSelectMultiple name="backoffices" label="Backoffice" options={userOptions} required />
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <FormSelectMultiple name="recruiters" label="Recruiter" options={userOptions} required />
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <FormSelectMultiple name="interviewers" label="Interviewer" options={userOptions} required />
            </Col>
          </Row>
        </Col>
        <Col xs={24} lg={12} xl={13}>
          <StyledRow justifyContent="flex-start">
            <FormCheckbox label="Active" name="active" />
            <FormCheckbox label="Internal" name="internal" />
          </StyledRow>

          <FormTextArea name="description" label="Description" required rows={18} />

          <Upload
            listType="picture-card"
            beforeUpload={() => false}
            fileList={attachments}
            onChange={({ fileList }) => {
              onAttachmentsChange(fileList);
            }}
            onDownload={(file) =>
              file.originFileObj instanceof Blob
                ? triggerDownload({ blob: file.originFileObj, filename: file.name })
                : setDownloadedFileId(file.uid)
            }
            itemRender={(node, file, list, actions) => (
              <div className="ant-upload-list-item">
                <div className="ant-upload-list-item-thumbnail ant-upload-list-item-file">
                  <PaperClipOutlined />
                </div>

                <span className="ant-upload-list-item-name" title={file.name}>
                  {file.name}
                </span>

                <span className="ant-upload-list-item-actions">
                  <DownloadOutlined onClick={actions.download} />
                  <DeleteOutlined onClick={actions.remove} />
                </span>
              </div>
            )}
          >
            <Button icon={<AddIcon />}>
              Add
            </Button>
          </Upload>
        </Col>
      </Row>
    </Form>
  );
};

const triggerDownload = ({ blob, filename }: { blob: Blob; filename: string }) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
